import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, MapPin, Phone, Package, ArrowLeft, Download, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch booking details
      const bookingResponse = await axios.get(`${API}/booking/${bookingId}`);
      const bookingData = bookingResponse.data;
      setBooking(bookingData);
      
      // Fetch vendor details
      const vendorResponse = await axios.get(`${API}/vendor/${bookingData.vendor_id}`);
      setVendor(vendorResponse.data);
      
    } catch (error) {
      console.error('Error fetching booking details:', error);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      })
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-green-100 text-green-800',
      'picked_up': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-purple-100 text-purple-800',
      'ready': 'bg-indigo-100 text-indigo-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusDisplay = (status) => {
    const displays = {
      'pending': 'Pending Confirmation',
      'confirmed': 'Confirmed',
      'picked_up': 'Picked Up',
      'in_progress': 'In Progress',
      'ready': 'Ready for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return displays[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!booking || !vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Booking not found</p>
          <Link to="/vendors" className="btn-primary">
            Book New Service
          </Link>
        </div>
      </div>
    );
  }

  const pickupDateTime = formatDateTime(booking.pickup_date);
  const deliveryDateTime = booking.delivery_date ? formatDateTime(booking.delivery_date) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/vendors" className="text-teal-600 hover:text-teal-700 transition-colors">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Booking Confirmation</h1>
              <p className="text-gray-600">Booking ID: {booking.id}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Success Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-600" size={32} />
            <div>
              <h2 className="text-xl font-semibold text-green-800">Booking Confirmed!</h2>
              <p className="text-green-700">
                Your laundry service has been booked successfully. You'll receive a confirmation email shortly.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Booking Details */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Booking Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
                    {getStatusDisplay(booking.status)}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-teal-600" />
                    <span className="text-sm">Booked on: {formatDateTime(booking.created_at).date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package size={16} className="text-teal-600" />
                    <span className="text-sm">Total Amount: ${booking.total_amount.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Schedule Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="text-teal-600" size={20} />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border-l-4 border-teal-600 pl-4">
                    <h4 className="font-semibold text-gray-900">Pickup</h4>
                    <p className="text-sm text-gray-600">{pickupDateTime.date}</p>
                    <p className="text-sm font-medium text-teal-600">{pickupDateTime.time}</p>
                  </div>
                  
                  {deliveryDateTime && (
                    <div className="border-l-4 border-green-600 pl-4">
                      <h4 className="font-semibold text-gray-900">Delivery</h4>
                      <p className="text-sm text-gray-600">{deliveryDateTime.date}</p>
                      <p className="text-sm font-medium text-green-600">{deliveryDateTime.time}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Customer Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium">{booking.customer.name}</p>
                    <p className="text-sm text-gray-600">{booking.customer.email}</p>
                    <p className="text-sm text-gray-600">{booking.customer.phone}</p>
                  </div>
                  <div className="flex items-start gap-2 pt-2 border-t">
                    <MapPin size={16} className="text-teal-600 mt-1" />
                    <div>
                      <p className="text-sm font-medium">Pickup Address:</p>
                      <p className="text-sm text-gray-600">{booking.customer.address}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Vendor & Services */}
          <div className="space-y-6">
            {/* Vendor Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Service Provider</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">{vendor.name}</h3>
                  <p className="text-gray-600">{vendor.description}</p>
                  
                  <div className="space-y-2 pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-teal-600" />
                      <span className="text-sm">{vendor.address}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-teal-600" />
                      <span className="text-sm">{vendor.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-teal-600" />
                      <span className="text-sm">{vendor.operating_hours}</span>
                    </div>
                    {vendor.website && (
                      <div className="flex items-center gap-2">
                        <Globe size={16} className="text-teal-600" />
                        <a 
                          href={vendor.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-teal-600 hover:text-teal-700 underline"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Services Card */}
            <Card>
              <CardHeader>
                <CardTitle>Services Ordered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {booking.services.map((service, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b last:border-b-0">
                      <div>
                        <p className="font-medium">{service.service_name}</p>
                        <p className="text-sm text-gray-600">
                          {service.quantity_lbs} lbs × ${service.price_per_lb}/lb
                        </p>
                      </div>
                      <p className="font-semibold text-teal-600">
                        ${service.total_price.toFixed(2)}
                      </p>
                    </div>
                  ))}
                  
                  <div className="flex justify-between items-center pt-3 border-t font-semibold text-lg">
                    <span>Total:</span>
                    <span className="text-teal-600">${booking.total_amount.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Special Instructions */}
            {booking.special_instructions && (
              <Card>
                <CardHeader>
                  <CardTitle>Special Instructions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{booking.special_instructions}</p>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <Link to="/vendors" className="btn-primary w-full text-center block">
                Book Another Service
              </Link>
              <Button variant="outline" className="w-full">
                <Download size={16} className="mr-2" />
                Download Receipt
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BookingConfirmation;